/**
 * The load-time pallet cache (ID28 decision 0013), as pure functions.
 *
 * While a truck is being loaded, every pallet still in inventory for the
 * order's products is held on the tablet, so a scan is judged from memory —
 * the gun beeps the instant the code lands — instead of after a round trip.
 * The store owns the fetching and the timer; these decide what a cached row
 * means and how a scan is matched against it, so that can be tested with no
 * network at all.
 */

import type { PalletInfo } from './lookupMapping'

/** One row of GET /api/pallets/active — slim, by design. */
export type ActivePalletDto = {
  palletIdentifier?: string | null
  productNumber?: string | null
  productDescription?: string | null
  caseQuantity?: number | null
  onHold?: boolean | null
  holdComments?: string | null
  storageName?: string | null
  carryOver?: boolean | null
}

/** Identifiers are matched the way loadValidation matches them. */
export function palletKey(identifier: string): string {
  return identifier.trim().toUpperCase()
}

/**
 * The cache's identity: which partner's pallets, for which products. A trip
 * whose order wants the same products as the last one can keep the cache;
 * anything else starts afresh.
 */
export function cacheKey(partnerId: number, productNumbers: readonly string[]): string {
  const products = [...new Set(productNumbers.map(p => p.trim().toUpperCase()).filter(p => p !== ''))].sort()
  return `${partnerId}|${products.join(',')}`
}

/**
 * A cached row as the card and the rules see it.
 *
 * `shipped` is false by construction — the read returns pallets still in
 * inventory — and the fields the read does not carry (weight, received date,
 * commodity) are blanks the Load card does not show anyway.
 */
export function mapActivePallet(row: ActivePalletDto): PalletInfo {
  return {
    palletId: (row.palletIdentifier ?? '').trim(),
    productNumber: row.productNumber ?? '',
    productDescription: row.productDescription ?? '',
    commodity: '',
    caseQuantity: row.caseQuantity ?? 0,
    weight: 0,
    receivedDate: '',
    currentLocation: (row.storageName ?? '').trim() || '—',
    onHold: row.onHold ?? false,
    holdComments: row.holdComments ?? '',
    shipped: false,
    carryOver: row.carryOver ?? false,
  }
}

/** Rows keyed for a scan to find, blanks dropped. */
export function indexActivePallets(rows: ActivePalletDto[]): Map<string, PalletInfo> {
  const index = new Map<string, PalletInfo>()
  for (const row of rows) {
    const info = mapActivePallet(row)
    if (info.palletId === '') continue
    index.set(palletKey(info.palletId), info)
  }
  return index
}

/** The cached pallet for a scanned code, or null when the cache does not know it. */
export function findCached(index: Map<string, PalletInfo> | null, scanned: string): PalletInfo | null {
  if (!index) return null
  return index.get(palletKey(scanned)) ?? null
}

/**
 * Whether the server's fresh answer contradicts what the cache said.
 *
 * A hit is judged from the cache at once and CONFIRMED behind it, because two
 * tablets load from the same pool: the other one may have shipped this pallet,
 * or the office may have put it on hold, since the last refresh. The things
 * that can change a verdict are the things compared; a different description
 * is not a contradiction.
 */
export function contradicts(cached: PalletInfo, fresh: PalletInfo): boolean {
  return fresh.shipped !== cached.shipped
    || fresh.onHold !== cached.onHold
    || fresh.carryOver !== cached.carryOver
    || palletKey(fresh.productNumber) !== palletKey(cached.productNumber)
}

/**
 * How many carry-over pallets of a product the cache still holds that could
 * go on a truck: not on hold, and not among the identifiers given — the forks,
 * what is already loaded. This is what the carry-over-first rule (decision
 * 0016) counts, and why a scan waits for the cache: without it there is no
 * count, and a verdict from a guess would let the wrong pallet on.
 */
export function carryOversAvailable(
  index: Map<string, PalletInfo> | null,
  productNumber: string,
  excluding: readonly string[] = [],
): number {
  if (!index) return 0
  const product = palletKey(productNumber)
  const skip = new Set(excluding.filter(id => id !== '').map(palletKey))
  let count = 0
  for (const [key, info] of index) {
    if (skip.has(key)) continue
    if (!info.carryOver || info.onHold) continue
    if (palletKey(info.productNumber) !== product) continue
    count += 1
  }
  return count
}
